import qs from 'qs';

import { logError } from '@utils/axios/ErrorLogging';
import GuestProps from '@interfaces/guest/GuestProps';
import { PROFILE_ERROR } from '@constants/ErrorConstants';
import { ProfileFeedVOList } from '@interfaces/feeds/ProfileFeedVO';
import axiosInstance, { createDownloadElem, guestAxiosEnv } from '@helpers/Axios';
import ProfileContentVO from '@interfaces/contents/ProfileContentVO';
import ProfileRequestAccessVO, { GuestDetailsVO } from '@interfaces/profiles/ProfileRequestAccessVO';
import ProfileCustomFieldVO from '@interfaces/profiles/ProfileCustomFieldVO';
import ProfileGuestMetaDataVO from '@interfaces/profiles/ProfileGuestMetaDataVO';
import { toGuestDetailsVO, toVerifyAccessMetadata } from '@utils/profile/GuestFormFieldUtils';
import { getAnonymousViewerId, setAnonymousViewerId } from '@utils/profile/ProfileAccessManagementUtils';
import ProfileFAQVO, { ProfileFAQResponse } from '@interfaces/faq/ProfileFAQVO';
import { filterPersistedSectionIds } from '@utils/faq/FAQSectionUtils';
import { getGuestUserData } from '@utils/GeneralUtils';

const paramsSerializer = (param: Record<string, unknown>) => {
  return qs.stringify(param, { arrayFormat: 'repeat' });
};

const errorLogger = (e: unknown, method: string) => {
  logError(e, 'ProfileService.ts', method);
};

export type ContentSearchPayload = {
  tags: string[];
  contentType: Array<'DOCUMENT' | 'PROJECT'>;
  limit: number;
  offset: number;
};

export type ContentSearchResponse = ProfileContentVO[];

const FAQ_GET_PAGE_LIMIT = 20;

export const getInitialData = async (guestProps: GuestProps) => {
  try {
    const isPublicUser = guestProps?.isPublicUser;
    const isAccessVerified = guestProps?.isAccessVerified;
    const companyId = guestProps?.companyId;
    const profileId = guestProps?.profileId;

    const usePublicUrl = isPublicUser && !isAccessVerified;

    const url: string = `guest_v3/${usePublicUrl ? `public/${companyId}/profiles/${profileId}` : 'profiles'}/init${usePublicUrl ? '?isAccessVerified=false' : ''}`;
    const response = await axiosInstance().get(url);
    return response;
  } catch (e) {
    errorLogger(e, 'getInitialData');
    return undefined;
  }
};

export const getProfile = async (sharedViewId?: string) => {
  try {
    const url = `guest_v3/profiles/v2/get${sharedViewId && sharedViewId.trim() ? `?viewId=${sharedViewId}` : ''}`;
    const response = await axiosInstance().get(url);
    return response;
  } catch (e) {
    errorLogger(e, 'getProfile');
    return undefined;
  }
};

export const getPublicProfile = async (profileId: string, companyId: string, viewId?: string) => {
  try {
    const url = `guest_v3/public/${companyId}/profiles/v2/${profileId}/get${viewId ? `?viewId=${viewId}` : ''}`;
    const response = await axiosInstance().get(url);
    return response;
  } catch (e) {
    errorLogger(e, 'getPublicProfile');
    return undefined;
  }
};

export const getProfilePreference = async (isPublicUser: boolean, companyId?: string, fields?: string[]) => {
  try {
    const params: Record<string, unknown> = {
      fields: fields || [],
      isPublicUser
    };

    const url: string = `guest_v3/${isPublicUser ? `public/${companyId}/` : ''}profiles/preference`;
    const response = await axiosInstance().get(url, { params, paramsSerializer });
    return response.data;
  } catch (e) {
    errorLogger(e, 'getProfilePreference');
    return undefined;
  }
};

export const getWatermarkDetails = async (fields: string[], isPublicUser?: boolean, companyId?: string) => {
  try {
    const params: Record<string, unknown> = {
      fields: fields || []
    };
    if (isPublicUser) {
      params['isPublicUser'] = true;
    }
    const url: string = `guest_v3/${isPublicUser ? `public/${companyId}/` : ''}profiles/watermark/get`;
    const response = await axiosInstance().get(url, { params, paramsSerializer });
    return response.data;
  } catch (e) {
    errorLogger(e, 'getWatermarkDetails');
    return undefined;
  }
};

export const getWatermark = async (fields: string[], onSuccess: (response: unknown) => void, onFailed?: () => void, isPublicUser?: boolean, companyId?: string) => {
  try {
    const params: Record<string, unknown> = {
      fields: fields || []
    };
    if (isPublicUser) {
      params['isPublicUser'] = true;
    }
    const url: string = `guest_v3/${isPublicUser ? `public/${companyId}/` : ''}profiles/watermark/get`;
    const response = await axiosInstance().get(url, { params, paramsSerializer });
    if (response && response.status === 200 && response.data) {
      onSuccess(response.data);
    } else {
      onFailed?.();
    }
  } catch (e) {
    onFailed?.();
    errorLogger(e, 'getWatermark');
  }
};

export const generateWatermark = async (postBody: { [key: string]: string | undefined }, guestProps: GuestProps, onSuccess: (result: unknown) => void, onFailed: () => void) => {
  try {
    const { isPublicUser, companyId, isAccessVerified, guestKey } = guestProps;
    const baseUrl = `guest_v3/${isPublicUser ? `public/${companyId}/` : ''}profiles/watermark/generate-watermark`;
    const queryParams = isPublicUser ? `?isPublicUser=true&isAccessVerified=${Boolean(isAccessVerified)}${isAccessVerified ? `&guestKey=${guestKey}` : ''}` : `?guestKey=${guestKey}`;
    const response = await axiosInstance().post(`${baseUrl}${queryParams}`, postBody);
    if (response && response.status === 200 && response.data) {
      onSuccess(response.data);
    } else {
      onFailed();
    }
  } catch (e) {
    onFailed();
    errorLogger(e, 'generateWatermark');
  }
};

export const generateWatermarkAsync = (postBody: { [key: string]: string | undefined }, guestProps: GuestProps) => {
  return new Promise<unknown>((resolve, reject) => {
    generateWatermark(
      postBody,
      guestProps,
      result => resolve(result),
      () => reject(new Error('Failed to generate watermark'))
    );
  });
};

export const getWatermarkBatchDetails = async (batchId: string, callBackFunc: (result: unknown) => void, onFailed: () => void, isPublicUser?: boolean, companyId?: string) => {
  try {
    const url: string = `guest_v3/${isPublicUser ? `public/${companyId}/` : ''}profiles/watermark/get-batch-details${isPublicUser ? '?isPublicUser=true' : ''}`;
    const response = await axiosInstance().get(url, { params: { id: batchId } });
    if (response && response.status === 200) {
      callBackFunc(response.data);
    } else {
      onFailed();
    }
  } catch (e) {
    onFailed();
    errorLogger(e, 'getWatermarkBatchDetails');
  }
};

export const getWatermarkBatchDetailsAsync = (batchId: string, isPublicUser?: boolean, companyId?: string) => {
  return new Promise<unknown>((resolve, reject) => {
    getWatermarkBatchDetails(
      batchId,
      result => resolve(result),
      () => reject(new Error('Failed to get watermark batch details')),
      isPublicUser,
      companyId
    );
  });
};

export const getContentSourceStatus = async (profileId: string, sectionId: string, contentId: string | string[], isPublicUser: boolean, companyId: string) => {
  try {
    const url: string = `guest_v3/${isPublicUser ? `public/${companyId}/` : ''}profiles/${profileId}/sections/${sectionId}/content/map-source-status${isPublicUser ? '?isPublicUser=true' : ''}`;
    const response = await axiosInstance().post(url, typeof contentId === 'string' ? [contentId] : contentId);
    return response.data;
  } catch (e) {
    errorLogger(e, 'getContentSourceStatus');
    return undefined;
  }
};

export const getContentSourceData = async (profileId: string, sectionId: string, contentId: string, isPublicUser: boolean, companyId: string, anonymousViewId: string | null) => {
  try {
    const sourceDataUrl = `${profileId}/sections/${sectionId}/content/${contentId}/get-source-data${isPublicUser ? '?isPublicUser=true' : ''}`;
    let url: string = `guest_v3/${isPublicUser ? `public/${companyId}/` : ''}profiles/${sourceDataUrl}`;
    if (anonymousViewId) {
      url += url.includes('?') ? `&anonymousViewId=${anonymousViewId}` : `?anonymousViewId=${anonymousViewId}`;
    }
    const response = await axiosInstance().get(url);
    return response.data;
  } catch (e) {
    errorLogger(e, 'getContentSourceData');
    return undefined;
  }
};

export const getBatchStatusForContents = async (
  profileId: string,
  sectionId: string,
  contentIds: string[],
  onSuccess: (response: { [key: string]: string[] }) => void,
  onFailed: () => void,
  isPublicUser?: boolean,
  companyId?: string
) => {
  try {
    const url: string = `guest_v3/${isPublicUser ? `public/${companyId}/` : ''}profiles/${profileId}/sections/${sectionId}/content/map-source-status${isPublicUser ? '?isPublicUser=true' : ''}`;
    const response = await axiosInstance().post(url, contentIds);
    if (response && response.status === 200 && response.data) {
      onSuccess(response.data);
    } else {
      onFailed();
    }
  } catch (e) {
    onFailed();
    errorLogger(e, 'getBatchStatusForContents');
  }
};

export const getContentByIds = async (
  profileId: string,
  sectionId: string,
  contentIds: string[],
  onSuccess: (response: ProfileContentVO[]) => void,
  onFailed?: () => void,
  isPublicUser?: boolean,
  companyId?: string
) => {
  try {
    const url: string = `guest_v3/${isPublicUser ? `public/${companyId}/` : ''}profiles/${profileId}/sections/${sectionId}/content/get-by-ids${isPublicUser ? '?isPublicUser=true' : ''}`;
    const response = await axiosInstance().post(url, contentIds);
    if (response && response.status === 200 && response.data) {
      onSuccess(response.data);
    }
  } catch (e) {
    onFailed?.();
    errorLogger(e, 'getContentByIds');
  }
};

export const searchSectionContents = async (
  profileId: string,
  searchQueryVO: ContentSearchPayload,
  callBackFunc: (result: ContentSearchResponse) => void,
  onFailed: (error: Error | string) => void,
  isPublicUser?: boolean,
  companyId?: string
) => {
  try {
    const url: string = `guest_v3/${isPublicUser ? `public/${companyId}/` : ''}profiles/${profileId}/contents/search${isPublicUser ? '?isPublicUser=true' : ''}`;
    const response = await axiosInstance().post(url, searchQueryVO);
    if (response && response.status === 200 && response.data) {
      callBackFunc(response.data as ContentSearchResponse);
    } else {
      onFailed(String(response?.data ?? ''));
    }
  } catch (e) {
    const err = e instanceof Error ? e : String(e);
    onFailed(err);
    errorLogger(err, 'searchSectionContents');
  }
};

export const handleProfileDownload = async (downloadUrl: string, anonymousViewId?: string | null, onFailed?: (error: string) => void, onSuccess?: () => void) => {
  try {
    let url: string = new URL(window.location.href).origin + downloadUrl;
    url += downloadUrl.includes('?') ? '&isDownloadCheck=true' : '?isDownloadCheck=true';
    const response = await axiosInstance().get(url);
    if (response && response.status === 200) {
      if (anonymousViewId) {
        downloadUrl += downloadUrl.includes('?') ? `&anonymousViewId=${anonymousViewId}` : `?anonymousViewId=${anonymousViewId}`;
      }
      downloadUrl += downloadUrl.includes('?') ? '&skipToken=true' : '?skipToken=true';
      createDownloadElem(downloadUrl, onSuccess);
    } else if (response) {
      onFailed?.((typeof response.data === 'string' ? response.data : response.data?.error) || '');
    } else {
      // do nothing
    }
  } catch (e) {
    onFailed?.(PROFILE_ERROR.SERVER_ERROR);
    errorLogger(e, 'handleProfileDownload');
  }
};

export const logUserActivity = async (profileId: string, companyId: string, activityType: string, anonymousViewId: string | null = null) => {
  try {
    let url: string = `guest_v3/public/${companyId}/profiles/${profileId}/log-user-activity`;
    const isAccessVerified: boolean = JSON.parse(sessionStorage.getItem('guestAccessVerified') || 'false');
    if (anonymousViewId && !isAccessVerified) {
      url += `?anonymousViewId=${anonymousViewId}`;
    }
    await axiosInstance().post(url, { activityType });
  } catch (e) {
    errorLogger(e, 'logUserActivity');
  }
};

export const onAgreeNda = async (
  isPublicUser: boolean,
  postBody: { [key: string]: string | null },
  callBackFunc: (result: unknown) => void,
  onFailed: (error: string) => void,
  companyId?: string,
  profileId?: string
) => {
  try {
    if (isPublicUser) {
      postBody['anonymousViewer'] = getAnonymousViewerId();
    }
    const url: string = isPublicUser ? `guest_v3/public/${companyId}/profiles/${profileId}/agree-nda` : 'guest_v3/profiles/agree-nda';
    const response = await axiosInstance().post(url, postBody);
    if (response && response.status === 200) {
      if (response.data?.additionalParams?.anonymousViewer && postBody.anonymousViewer !== response.data.additionalParams.anonymousViewer) {
        setAnonymousViewerId(response.data.additionalParams.anonymousViewer, true);
      }
      callBackFunc(response.data);
    } else if (response && response.data.additional_message) {
      onFailed(response.data.additional_message);
    } else {
      // do nothing
    }
  } catch (e) {
    errorLogger(e, 'onAgreeNda');
  }
};

export const reuploadContent = async (profileId: string, sectionId: string, contentId: string, onSuccess: (result: ProfileContentVO) => void, onFailed: () => void, guestProps: GuestProps) => {
  try {
    const response = await axiosInstance().post(`guest_v3/public/${guestProps.companyId}/profiles/${profileId}/sections/${sectionId}/content/${contentId}/reupload`);
    if (response && response.status === 200 && response.data) {
      onSuccess(response.data);
    } else {
      onFailed();
    }
  } catch (e) {
    onFailed();
    errorLogger(e, 'reuploadContent');
  }
};

export const reuploadContentAsync = (profileId: string, sectionId: string, contentId: string, guestProps: GuestProps) => {
  return new Promise<ProfileContentVO>((resolve, reject) => {
    reuploadContent(
      profileId,
      sectionId,
      contentId,
      result => resolve(result),
      () => reject(new Error('Failed to re-upload content')),
      guestProps
    );
  });
};

export const subscribeProfile = async (callBackFunc: () => void) => {
  try {
    const response = await axiosInstance().post('guest_v3/profiles/subscribe');
    if (response && response.status === 200) {
      callBackFunc();
    }
  } catch (e) {
    errorLogger(e, 'subscribe');
  }
};

export const subscribePublicProfile = async (postBody: { [key: string]: string | null }, companyId: string, profileId: string, callBackFunc: (result: ProfileRequestAccessVO) => void) => {
  try {
    postBody['anonymousViewer'] = getAnonymousViewerId();
    const response = await axiosInstance().post(`guest_v3/public/${companyId}/profiles/${profileId}/subscribe`, postBody);
    if (response && response.status === 200 && response.data) {
      callBackFunc(response.data);
    }
  } catch (e) {
    errorLogger(e, 'subscribePublicProfile');
  }
};

export const logGuestEntry = async (postBody: { [key: string]: string | null }, companyId: string, profileId: string, callBackFunc: (result: ProfileRequestAccessVO) => void) => {
  try {
    postBody['anonymousViewer'] = getAnonymousViewerId();
    const response = await axiosInstance().post(`guest_v3/public/${companyId}/profiles/${profileId}/log-guest-entry`, postBody);
    if (response && response.status === 200 && response.data) {
      callBackFunc(response.data);
    }
  } catch (e) {
    errorLogger(e, 'logGuestEntry');
  }
};

export const unSubscribeProfile = async (companyId: string, profileId: string, mailId: string, isPublicUser: boolean, reason: string, callBackFunc: (result: ProfileRequestAccessVO) => void) => {
  try {
    const url: string = isPublicUser ? `guest_v3/public/${companyId}/profiles/${profileId}/unsubscribe` : 'guest_v3/profiles/unsubscribe';
    const postBody: { [key: string]: string | undefined } = isPublicUser ? { unsubscribedReason: reason, mailId } : { unsubscribedReason: reason };
    const response = await axiosInstance().post(url, postBody);
    if (response && response.status === 200) {
      callBackFunc(response.data);
    }
  } catch (e) {
    errorLogger(e, 'unsubscribe');
  }
};

export const getProfileFeeds = async (companyId: string, profileId: string, isPublicUser: boolean, searchQuery: Record<string, unknown>, callBackFunc: (result: ProfileFeedVOList) => void) => {
  try {
    const url: string = isPublicUser ? `guest_v3/public/${companyId}/profiles/${profileId}/feeds` : 'guest_v3/profiles/feeds';
    const response = await axiosInstance().post(url, searchQuery);
    if (response && response.status === 200 && response.data) {
      callBackFunc(response.data);
    } else {
      callBackFunc({ profileFeedVOList: [], totalCount: 0 });
    }
  } catch (e) {
    errorLogger(e, 'getProfileFeeds');
  }
};

export const getFAQs = async (
  companyId: string,
  profileId: string,
  isPublicUser: boolean,
  nextCursor: string | undefined,
  viewId: string | undefined,
  callBackFunc: (result: ProfileFAQResponse) => void,
  sectionIds?: string[]
) => {
  const normalizedSectionIds = filterPersistedSectionIds(sectionIds);
  try {
    let url: string = isPublicUser ? `guest_v3/public/${companyId}/profiles/${profileId}/FAQ` : 'guest_v3/profiles/FAQ';
    if (viewId) {
      url += `?viewId=${viewId}`;
    }
    const response = await axiosInstance().get(url, {
      params: {
        ...(nextCursor ? { nextCursor } : {}),
        limit: FAQ_GET_PAGE_LIMIT,
        ...(normalizedSectionIds.length ? { sectionIds: normalizedSectionIds } : {})
      },
      ...(normalizedSectionIds.length ? { paramsSerializer } : {})
    });
    if (response && response.status === 200 && response.data) {
      callBackFunc(normalizeGuestFAQGetResponse(response.data));
    } else if (response && response.status === 204) {
      callBackFunc(normalizeGuestFAQGetResponse(undefined));
    } else {
      // do nothing
    }
  } catch (e) {
    errorLogger(e, 'getFAQs');
  }
};

export const getSubProcessors = async (companyId: string, profileId: string, isPublicUser: boolean, callBackFunc: (result: ProfileFAQResponse) => void, viewId?: string) => {
  try {
    let url: string = isPublicUser ? `guest_v3/public/${companyId}/profiles/${profileId}/SUB_PROCESSOR` : 'guest_v3/profiles/SUB_PROCESSOR';
    if (viewId) {
      url += `?viewId=${viewId}`;
    }
    const response = await axiosInstance().get(url);
    if (response && response.status === 200 && response.data) {
      callBackFunc(response.data);
    } else if (response && response.status === 204) {
      callBackFunc({ artifacts: [], paginationVO: { hasNext: false }, addedSourceIds: [], hasFAQUpdate: false });
    } else {
      // do nothing
    }
  } catch (e) {
    errorLogger(e, 'caught from ProfileService from the method getSubProcessors');
  }
};

export const getNdaSignedStatus = async (isPublicUser?: boolean, companyId?: string, profileId?: string, mailId?: string) => {
  try {
    const postBody = {
      mailId
    };
    const url: string = isPublicUser ? `guest_v3/public/${companyId}/profiles/${profileId}/get-nda-signed-status` : 'guest_v3/profiles/get-nda-signed-status';
    const response = isPublicUser ? await axiosInstance().post(url, postBody) : await axiosInstance().get(url);
    return response;
  } catch (e) {
    errorLogger(e, 'getNdaSignedStatus');
    return undefined;
  }
};

export const getNdaMergeTags = async (isPublicUser: boolean, companyId?: string) => {
  try {
    const url: string = isPublicUser ? `guest_v3/public/${companyId}/profiles/get-nda-merge-tags?isPublicUser=true` : 'guest_v3/profiles/get-nda-merge-tags';
    const response = await axiosInstance().get(url);
    if (response.status === 200 && response.data) {
      return response.data;
    }
  } catch (e) {
    errorLogger(e, 'getNdaMergeTags');
    return [];
  }
};

export const getCustomFields = async (isPublicUser: boolean, companyId?: string, profileId?: string): Promise<ProfileCustomFieldVO[]> => {
  try {
    if (!profileId) {
      return [];
    }
    const url: string = isPublicUser ? `guest_v3/public/${companyId}/profiles/${profileId}/get-custom-fields?isPublicUser=true` : `guest_v3/profiles/${profileId}/get-custom-fields`;
    const response = await axiosInstance().get(url);
    if (response.status === 200 && response.data) {
      return response.data;
    }
    return [];
  } catch (e) {
    errorLogger(e, 'getCustomFields');
    return [];
  }
};

export const downloadContents = async (companyId: string, profileId: string, isPublicUser: boolean, payload: { [key: string]: string[] }, callBack: (response: unknown) => void) => {
  try {
    const isBulkDownload = (payload?.docIds?.length ?? 0) + (payload?.questionnaireIds?.length ?? 0) > 1;
    const anonymousViewerId = getAnonymousViewerId();
    const url: string = isPublicUser
      ? `guest_v3/public/${companyId}/profiles/${profileId}/download-content?isBulkDownload=${isBulkDownload}&anonymousViewerId=${anonymousViewerId}`
      : `guest_v3/profiles/download-content?isBulkDownload=${isBulkDownload}`;
    const response = await axiosInstance().post(url, payload);
    if (response) {
      callBack(response.data);
    }
  } catch (e) {
    errorLogger(e, 'downloadContents');
  }
};

export const getDownloadBatchDetails = async (companyId: string, profileId: string, batchId: string, isPublicUser: boolean, callBackFunc: (response: unknown) => void) => {
  try {
    const url: string = `guest_v3/${isPublicUser ? `public/${companyId}/` : ''}profiles${isPublicUser ? `/${profileId}` : ''}/get-batch-details${isPublicUser ? '?isPublicUser=true' : ''}`;
    const response = await axiosInstance().get(url, { params: { id: batchId } });
    if (response && response.status === 200) {
      callBackFunc(response.data);
    }
  } catch (e) {
    errorLogger(e, 'getDownloadBatchDetails');
  }
};

export const trackAnalyticsForBulkDownload = (profileId: string, contentIds: string[], isPublicUser: boolean, guestProps: GuestProps) => {
  try {
    let url = `guest_v3/public/${guestProps.companyId}/profiles/${profileId}/bulk-download/track-analytics?isPublicUser=${isPublicUser}`;
    const anonymousViewerId = getAnonymousViewerId();
    url += anonymousViewerId && isPublicUser ? `&anonymousViewerId=${anonymousViewerId}` : `&guestKey=${guestProps.guestKey}`;

    const currentUser = getGuestUserData();
    const isAccessVerified = JSON.parse(sessionStorage.getItem('guestAccessVerified') || 'false');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Accept: 'application/json, text/plain, */*',
      ...(currentUser.accessKey &&
        (!isPublicUser || isAccessVerified) && {
          authorization: `Bearer ${currentUser.accessKey}`,
          'guest-key': currentUser.guestKey
        }),
      ...(guestAxiosEnv.podName && { 'pod-name': guestAxiosEnv.podName })
    };

    fetch(`/rfpserver/${url}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ contentIds }),
      keepalive: true
    }).catch(e => errorLogger(e, 'trackAnalyticsForBulkDownload'));
  } catch (e) {
    errorLogger(e, 'trackAnalyticsForBulkDownload');
  }
};

export const getCustomFonts = async (companyId: string, profileId: string, isPublicUser: boolean) => {
  try {
    return await axiosInstance().get(`guest_v3/public/${companyId}/profiles/${profileId}/get-custom-fonts?isPublicUser=${isPublicUser}`);
  } catch (e) {
    errorLogger(e, 'getCustomFonts');
    return undefined;
  }
};

export const getSigningUrl = async (companyId: string, payload: { [key: string]: string }, callBack: (response: { [key: string]: string }) => void) => {
  try {
    const response = await axiosInstance().post(`guest_v3/public/${companyId}/profiles/get-signing-url`, payload);
    if (response && response.status === 200 && response.data) {
      callBack(response.data);
    }
  } catch (e) {
    errorLogger(e, 'getSigningUrl');
  }
};

export const updateSignerNameInEnvelope = async (companyId: string, isPublicUser: boolean, payload: { [key: string]: string }, callBack: (response: Record<string, unknown>) => void) => {
  try {
    const url: string = `guest_v3/${isPublicUser ? `public/${companyId}/` : ''}profiles/update-docusign-recipient?isPublicUser=${isPublicUser}`;
    const response = await axiosInstance().post(url, payload);
    if (response) {
      callBack(response.data);
    }
  } catch (e) {
    errorLogger(e, 'updateSignerNameInEnvelope');
  }
};

export const onSigningDocusignNda = async (companyId: string, profileId: string, isPublicUser: boolean, payload: { [key: string]: string }, callBack: (response: ProfileRequestAccessVO) => void) => {
  try {
    const url = `guest_v3/${isPublicUser ? `public/${companyId}/` : ''}profiles/${profileId}/docusign/agree-nda?isPublicUser=${isPublicUser}`;
    const response = await axiosInstance().post(url, payload);
    if (response) {
      callBack(response.data);
    }
  } catch (e) {
    errorLogger(e, 'onSigningDocusignNda');
  }
};

export const updateDocusignDeclinedDocusign = async (
  companyId: string,
  profileId: string,
  isPublicUser: boolean,
  payload: { [key: string]: string },
  callBack: (response: ProfileRequestAccessVO) => void
) => {
  try {
    const url = `guest_v3/${isPublicUser ? `public/${companyId}/` : ''}profiles/${profileId}/docusign/update-declined-status`;
    const response = await axiosInstance().post(url, payload);
    if (response && response.status === 200 && response.data) {
      callBack(response.data);
    } else {
      // do nothing
    }
  } catch (e) {
    errorLogger(e, 'updateDocusignDeclinedDocusign');
  }
};

export const getSessions = async (guestProps: GuestProps) => {
  try {
    const url: string = `guest_v3/${guestProps.isPublicUser ? `public/${guestProps.companyId}/` : ''}profiles/sessions${
      guestProps.isPublicUser ? `?isPublicUser=true&isAccessVerified=${Boolean(guestProps.isAccessVerified)}` : ''
    }`;
    const payload = {
      profileId: guestProps.profileId,
      guest: guestProps.mailId
    };
    const response = await axiosInstance().post(url, payload);
    return response;
  } catch (e) {
    errorLogger(e, 'getSessions');
    return undefined;
  }
};

export const searchTranslatedProfiles = async (searchQueryVO: Record<string, unknown>, callBackFunc: (result: Record<string, unknown>) => void, guestProps: GuestProps) => {
  try {
    const { isPublicUser } = guestProps;
    const { companyId } = guestProps;
    let url = `guest_v3/${isPublicUser ? `public/${companyId}/` : ''}profiles/search-translated-profile`;

    if (isPublicUser) {
      const anonymousViewerId = getAnonymousViewerId();
      url += '?isPublicUser=true';
      if (guestProps?.isAccessVerified) {
        url += `&isAccessVerified=${Boolean(guestProps.isAccessVerified)}`;
        if (guestProps.guestKey) {
          url += `&guestKey=${guestProps.guestKey}`;
        }
      }
      if (anonymousViewerId) {
        url += `&anonymousViewerId=${anonymousViewerId}`;
      }
    } else if (guestProps?.guestKey) {
      url += `?guestKey=${guestProps.guestKey}`;
    }

    const response = await axiosInstance().post(url, searchQueryVO);

    if (response.status === 200 && response.data) {
      callBackFunc(response.data);
    } else {
      callBackFunc({ profiles: [], totalCount: 0 });
    }
  } catch (e) {
    callBackFunc({ profiles: [], totalCount: 0 });
    errorLogger(e, 'searchTranslatedProfiles');
  }
};

export const getGuestMetaData = async (companyId: string, profileId: string, mailId: string, isPublicUser: boolean, callBackFunc: (res: GuestDetailsVO | null) => void) => {
  try {
    const response = await axiosInstance().post(`guest_v3/public/${companyId}/profiles/${profileId}/get-guest-metadata?isPublicUser=${isPublicUser}`, { guest: mailId });
    if (response.status === 200 && response.data) {
      callBackFunc(toGuestDetailsVO(response.data as ProfileGuestMetaDataVO));
      return;
    }
    callBackFunc(null);
  } catch (e) {
    errorLogger(e, 'getGuestMetaData');
    callBackFunc(null);
  }
};

export const saveGuestMetaData = async (companyId: string, profileId: string, mailId: string, details: GuestDetailsVO, isPublicUser: boolean, callBackFunc?: (res: GuestDetailsVO | null) => void) => {
  const metadata = Object.entries(toVerifyAccessMetadata(details)).map(([fieldTag, value]) => ({ fieldTag, value }));
  if (!metadata.length) {
    callBackFunc?.(null);
    return false;
  }

  try {
    const response = await axiosInstance().post(`guest_v3/public/${companyId}/profiles/${profileId}/save-guest-metadata?isPublicUser=${isPublicUser}`, { guest: mailId, metadata });
    if (response?.status === 200) {
      callBackFunc?.(toGuestDetailsVO(response.data as ProfileGuestMetaDataVO));
      return true;
    }
    callBackFunc?.(null);
    return false;
  } catch (e) {
    errorLogger(e, 'saveGuestMetaData');
    callBackFunc?.(null);
    return false;
  }
};

const emptyGuestFAQResponse = (): ProfileFAQResponse => ({
  artifacts: [],
  hasFAQUpdate: false,
  paginationVO: { hasNext: false, page: 0, size: 0, totalElements: 0, totalPages: 0 },
  addedSourceIds: []
});

const buildGuestAggregatePagination = (sectionPagination: ProfileFAQResponse['sectionPagination']) => {
  const hasNext = Object.values(sectionPagination || {}).some(pagination => pagination?.hasNext);
  return {
    hasNext,
    ...(hasNext ? { nextCursor: Object.values(sectionPagination || {}).find(pagination => pagination?.hasNext)?.nextCursor } : {})
  };
};

export const mergeGuestFAQPage = (previous: ProfileFAQResponse | undefined, incoming: ProfileFAQResponse): ProfileFAQResponse => {
  const byId = new Map<string, ProfileFAQVO>();
  [...(previous?.artifacts || []), ...incoming.artifacts].forEach(faq => byId.set(faq.id, faq));

  const sectionFaqs = { ...(previous?.sectionFaqs || {}) };
  Object.entries(incoming.sectionFaqs || {}).forEach(([sectionId, faqs]) => {
    const sectionById = new Map((sectionFaqs[sectionId] || []).map(faq => [faq.id, faq]));
    (faqs || []).forEach(faq => sectionById.set(faq.id, faq));
    sectionFaqs[sectionId] = Array.from(sectionById.values());
  });

  const sectionPagination = { ...(previous?.sectionPagination || {}), ...(incoming.sectionPagination || {}) };

  return {
    ...incoming,
    artifacts: Array.from(byId.values()),
    sectionFaqs,
    sectionPagination,
    addedSourceIds: Array.from(new Set([...(previous?.addedSourceIds || []), ...incoming.addedSourceIds])),
    hasFAQUpdate: Boolean(previous?.hasFAQUpdate || incoming.hasFAQUpdate),
    paginationVO: {
      ...(incoming.paginationVO || previous?.paginationVO || emptyGuestFAQResponse().paginationVO),
      ...buildGuestAggregatePagination(sectionPagination)
    },
    allSubprocessorCategories: incoming.allSubprocessorCategories || previous?.allSubprocessorCategories
  };
};

const normalizeGuestFAQGetResponse = (raw: ProfileFAQResponse | null | undefined): ProfileFAQResponse => {
  if (!raw) {
    return emptyGuestFAQResponse();
  }
  if (!raw.sectionFaqs) {
    return {
      artifacts: raw.artifacts ?? [],
      hasFAQUpdate: raw.hasFAQUpdate ?? false,
      addedSourceIds: raw.addedSourceIds ?? [],
      paginationVO: raw.paginationVO ?? emptyGuestFAQResponse().paginationVO,
      allSubprocessorCategories: raw.allSubprocessorCategories
    };
  }

  const sectionFaqs = Object.fromEntries(Object.entries(raw.sectionFaqs).map(([sectionId, faqs]) => [sectionId, (faqs || []).map(faq => ({ ...faq, sectionId: faq.sectionId || sectionId }))]));
  const artifacts = Object.values(sectionFaqs).flat();

  return {
    artifacts,
    sectionFaqs,
    sectionPagination: raw.sectionPagination ?? {},
    hasFAQUpdate: raw.hasFAQUpdate ?? false,
    addedSourceIds: raw.addedSourceIds ?? [],
    paginationVO: {
      ...(raw.paginationVO || emptyGuestFAQResponse().paginationVO),
      ...buildGuestAggregatePagination(raw.sectionPagination)
    },
    allSubprocessorCategories: raw.allSubprocessorCategories
  };
};
